
export const primaryBlueColor = "#1672DE";

// Primary blue hover color
export const primaryBlueHoverColor = "#1565c0";

// Primary blue color with opacity (for backgrounds, hover effects)
export const primaryBlueColorLight = "rgba(22, 114, 222, 0.04)";
export const primaryBlueColorMedium = "rgba(22, 114, 222, 0.08)";
export const primaryBlueColorDark = "rgba(22, 114, 222, 0.12)";
export const primaryBlueColorBorder = "rgba(22, 114, 222, 0.15)";
export const primaryBlueColorShadow = "rgba(22, 114, 222, 0.2)";
export const primaryBlueColorShadowHover = "rgba(22, 114, 222, 0.3)";

// ============================================
// BUTTON STYLES
// ============================================

// Primary button style (Add, Submit, Update buttons)
export const primaryButtonStyle = {
  backgroundColor: primaryBlueColor,
  color: "white",
  borderRadius: "10px",
  padding: "10px 24px",
  fontWeight: "600",
  fontSize: "14px",
  textTransform: "none",
  minHeight: "40px",
  minWidth: "120px",
  boxShadow: `0 2px 8px ${primaryBlueColorShadow}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: primaryBlueHoverColor,
    boxShadow: `0 4px 12px ${primaryBlueColorShadowHover}`,
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  "&:disabled": {
    backgroundColor: "#CCCCCC",
    color: "#ffffff",
    cursor: "not-allowed",
  },
};

// Secondary button style (Reset, Cancel buttons)
export const secondaryButtonStyle = {
  backgroundColor: "white",
  color: "black",
  border: "1px solid black",
  borderRadius: "20px",
  padding: "16px 0px",
  width: "90px",
  gap: "8px",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
};

// Download/Export button style
export const downloadButtonStyle = {
  backgroundColor: primaryBlueColor,
  color: "white",
  borderRadius: "10px",
  padding: "10px 24px",
  fontWeight: "600",
  fontSize: "14px",
  textTransform: "none",
  minHeight: "40px",
  minWidth: "120px",
  boxShadow: `0 2px 8px ${primaryBlueColorShadow}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: primaryBlueHoverColor,
    boxShadow: `0 4px 12px ${primaryBlueColorShadowHover}`,
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  "&:disabled": {
    backgroundColor: "#CCCCCC",
    color: "#ffffff",
    cursor: "not-allowed",
  },
};

// Update button style (for Edit dialogs)
export const updateButtonStyle = {
  backgroundColor: primaryBlueColor,
  width: "90px",
  padding: "18px 0px",
  borderRadius: "20px",
  gap: "8px",
  color: "white",
  "&:hover": {
    backgroundColor: primaryBlueHoverColor,
  },
  "&:disabled": {
    backgroundColor: "#CCCCCC",
    color: "#ffffff",
    cursor: "not-allowed",
  },
};

// Submit button style (for Add dialogs)
export const submitButtonStyle = {
  backgroundColor: primaryBlueColor,
  width: "90px",
  padding: "17px 0px",
  borderRadius: "13px",
  gap: "8px",
  color: "white",
  "&:hover": {
    backgroundColor: primaryBlueHoverColor,
  },
};

// Delete button style (conditional - enabled when selectAll is true)
export const getDeleteButtonStyle = (selectAll) => ({
  backgroundColor: selectAll ? primaryBlueColor : "#CCCCCC",
  color: selectAll ? "white" : "black",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "17px 10px",
  borderRadius: "15px",
  "&:hover": {
    background: selectAll ? primaryBlueHoverColor : "#CCCCCC",
  },
  "&:disabled": {
    backgroundColor: "#CCCCCC",
    color: "#666666",
    cursor: "not-allowed",
  },
});

// Add button style (for modal dialogs)
export const addButtonStyle = {
  backgroundColor: primaryBlueColor,
  color: "white",
  borderRadius: "10px",
  padding: "8px 16px",
  "&:hover": {
    backgroundColor: primaryBlueHoverColor,
  },
};

// Hover color for all primary buttons (alias for consistency)
export const primaryButtonHoverColor = primaryBlueHoverColor;

// ============================================
// CHECKBOX STYLES
// ============================================

// Checkbox style for blue theme (unchecked state)
export const checkboxStyle = {
  color: primaryBlueColor,
  "&.Mui-checked": {
    color: primaryBlueColor,
  },
  "&.MuiCheckbox-colorPrimary": {
    color: primaryBlueColor,
    "&.Mui-checked": {
      color: primaryBlueColor,
    },
  },
};

// Checkbox style for white (on blue background)
export const checkboxWhiteStyle = {
  color: "white",
  "&.Mui-checked": {
    color: "white",
  },
  "& .MuiSvgIcon-root": {
    color: "white",
    fill: "white",
  },
  "&.Mui-checked .MuiSvgIcon-root": {
    color: "white",
    fill: "white",
  },
};

// ============================================
// TEXT FIELD / INPUT STYLES
// ============================================

// TextField focus style (blue border on focus)
export const textFieldFocusStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#d0d0d0",
    },
    "&:hover fieldset": {
      borderColor: primaryBlueColor,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryBlueColor,
      borderWidth: "2px",
    },
  },
};

// ============================================
// TABLE HEADER STYLES
// ============================================

// Table header background color
export const tableHeaderBackgroundColor = primaryBlueColor;

// Table header text color
export const tableHeaderTextColor = "white";

